import { BaseMapController } from './core/BaseMapController';
import { MarkerManager } from './managers/MarkerManager';
import { LineManager } from './managers/LineManager';
import { PolygonManager } from './managers/PolygonManager';
import { HistoryFeatureManager } from './managers/HistoryFeatureManager';
import { GeometryUtils } from './utils/GeometryUtils';
import {
  IMapController,
  IMapData,
  IMarkerConfig,
  ILineConfig,
  IPolygonConfig,
  IMapFeatures,
  IHistoryConfig,
  ICoordinate,
  MapEventType,
  MapEventHandler,
  ICompositeMapControllerProps,
} from './types';
import { IPlan } from '../../types/plans';
import { HistoryManager } from './history';

export interface IMapFeatureConfig {
  markers?: boolean;
  lines?: boolean;
  polygons?: boolean;
  geocoder?: boolean;
  history?: boolean | IHistoryConfig;
}

/**
 * CompositeMapController - Main map controller with feature composition
 * Combines BaseMapController with feature managers based on configuration
 */
export class CompositeMapController implements IMapController {
  private baseController: BaseMapController;
  private managers: Map<string, unknown> = new Map();

  private markerManager?: MarkerManager;
  private lineManager?: LineManager;
  private polygonManager?: PolygonManager;
  private historyManager?: HistoryFeatureManager;

  constructor(props: ICompositeMapControllerProps) {
    this.baseController = new BaseMapController(props);

    this.onLoad = this.baseController.onLoad;
    this.initializeFeatures(props.features || {});
  }

  /**
   * Executes a function when the map style is ready
   * If the map is already ready, executes immediately
   * Otherwise, waits for the style to load
   */
  private executeWhenReady(operation: () => void): void {
    if (this.baseController.isReady) {
      operation();
    } else {
      console.warn(
        'Map style not loaded yet, waiting for style to load before executing operation'
      );
      this.baseController.getMap().once('styledata', () => {
        operation();
      });
    }
  }

  private initializeFeatures(features: IMapFeatureConfig): void {
    if (features.geocoder) {
      this.baseController.configureGeocoder(true);
    }

    if (features.markers) {
      this.markerManager = new MarkerManager(this.baseController);
      this.managers.set('markers', this.markerManager);
    }

    if (features.lines) {
      this.lineManager = new LineManager(this.baseController);
      this.managers.set('lines', this.lineManager);
    }

    if (features.polygons) {
      this.polygonManager = new PolygonManager(this.baseController);
      this.managers.set('polygons', this.polygonManager);
    }

    if (features.history) {
      const historyConfig =
        typeof features.history === 'boolean' ? { enabled: features.history } : features.history;

      if (this.markerManager || this.lineManager || this.polygonManager) {
        this.historyManager = new HistoryFeatureManager(
          this.baseController,
          this.markerManager || new MarkerManager(this.baseController),
          this.lineManager || new LineManager(this.baseController),
          this.polygonManager || new PolygonManager(this.baseController),
          historyConfig
        );
        this.managers.set('history', this.historyManager);
      } else {
        console.warn(
          '⚠️ History feature requires at least one other feature (markers, lines, or polygons)'
        );
      }
    }
  }

  get isReady(): boolean {
    return this.baseController.isReady;
  }

  get loading(): boolean {
    return this.baseController.loading;
  }

  set loading(value: boolean) {
    this.baseController.loading = value;
  }

  get data(): IMapData {
    return this.baseController.data;
  }

  get isMobile(): boolean {
    return this.baseController.isMobile;
  }

  onLoad: (value: boolean) => void;

  get geometryUtils() {
    return GeometryUtils;
  }

  static get utils() {
    return GeometryUtils;
  }

  flyTo(center: [number, number]) {
    this.baseController.flyTo(center);
  }

  getMap() {
    return this.baseController.getMap();
  }

  onChange(): void {
    this.baseController.onChange();
  }

  setHandler(handler: (data: IMapData) => void): void {
    this.baseController.setHandler(handler);
  }

  takeScreenshot(): Promise<File> {
    return this.baseController.takeScreenshot();
  }

  enableGeocoder(): void {
    this.baseController.enableGeocoder();
  }

  configureGeocoder(enabled: boolean): void {
    this.baseController.configureGeocoder(enabled);
  }

  addGeocoderListener(eventType: MapEventType, handler: MapEventHandler): void {
    this.baseController.addGeocoderListener(eventType, handler);
  }

  removeGeocoderListener(eventType: MapEventType, handler: MapEventHandler): void {
    this.baseController.removeGeocoderListener(eventType, handler);
  }

  setGeocoderAddress(address: string): void {
    this.baseController.setGeocoderAddress(address);
  }

  clearGeocoderAddress(): void {
    this.baseController.clearGeocoderAddress();
  }

  setPlan(plan: Partial<IPlan>): void {
    this.baseController.setPlan(plan);
  }

  drawPlan(): void {
    this.baseController.drawPlan();
  }

  clear(): void {
    this.baseController.clear();
  }

  clearRotateLayer(): void {
    this.baseController.clearRotateLayer();
  }

  createRotateLayer(): void {
    this.baseController.createRotateLayer();
  }

  setRotation(rotation: number): void {
    this.baseController.setRotation(rotation);
  }

  addMarker(marker: IMarkerConfig): void {
    if (!this.markerManager) {
      console.warn('Marker manager not initialized. Enable markers in features config.');
      return;
    }

    if (this.historyManager?.isEnabled) {
      this.historyManager.addMarker(marker);
    } else {
      this.markerManager.addMarker(marker);
    }
  }

  removeMarker(markerId: string): void {
    if (!this.markerManager) {
      console.warn('Marker manager not initialized.');
      return;
    }

    if (this.historyManager?.isEnabled) {
      this.historyManager.removeMarker(markerId);
    } else {
      this.markerManager.removeMarker(markerId);
    }
  }

  updateMarker(marker: IMarkerConfig): void {
    if (!this.markerManager) {
      console.warn('Marker manager not initialized.');
      return;
    }

    if (this.historyManager?.isEnabled) {
      this.historyManager.updateMarker(marker);
    } else {
      this.markerManager.updateMarker(marker);
    }
  }

  getMarkers(): IMarkerConfig[] {
    if (!this.markerManager) return [];
    return this.markerManager.getMarkers();
  }

  getMarker(markerId: string): IMarkerConfig | undefined {
    if (!this.markerManager) return undefined;
    return this.markerManager.getMarker(markerId);
  }

  clearMarkers(): void {
    if (!this.markerManager) return;
    this.markerManager.clearMarkers();
  }

  setMarkerClickHandler(
    markerId: string,
    handler: (markerId: string, screenPos: { x: number; y: number }) => void
  ): void {
    if (!this.markerManager) return;
    this.markerManager.setMarkerClickHandler(markerId, handler);
  }

  removeMarkerClickHandler(markerId: string): void {
    if (!this.markerManager) return;
    this.markerManager.removeMarkerClickHandler(markerId);
  }

  setMarkerDragHandlers(
    markerId: string,
    handlers: {
      onDragStart?: (id: string, coordinate: ICoordinate) => void;
      onDrag?: (id: string, coordinate: ICoordinate) => void;
      onDragEnd?: (id: string, oldCoordinate: ICoordinate, newCoordinate: ICoordinate) => void;
    }
  ): void {
    if (!this.markerManager) return;
    this.markerManager.setMarkerDragHandlers(markerId, handlers);
  }

  removeMarkerDragHandlers(markerId: string): void {
    if (!this.markerManager) return;
    this.markerManager.removeMarkerDragHandlers(markerId);
  }

  moveMarker(markerId: string, oldCoordinate: ICoordinate, newCoordinate: ICoordinate): void {
    if (!this.historyManager?.isEnabled) {
      const marker = this.markerManager?.getMarker(markerId);
      if (marker) {
        const updatedMarker = { ...marker, coordinate: newCoordinate };
        this.markerManager?.updateMarker(updatedMarker);
      }
      return;
    }
    this.historyManager.moveMarker(markerId, oldCoordinate, newCoordinate);
  }

  movePolygonCorner(
    markerId: string,
    oldCoordinate: ICoordinate,
    newCoordinate: ICoordinate,
    oldPolygon: IPolygonConfig,
    newPolygon: IPolygonConfig
  ): void {
    if (!this.historyManager?.isEnabled) {
      const marker = this.markerManager?.getMarker(markerId);
      if (marker) {
        const updatedMarker = { ...marker, coordinate: newCoordinate };
        this.markerManager?.updateMarker(updatedMarker);
      }
      if (this.polygonManager) {
        this.polygonManager.updatePolygon(newPolygon);
      }
      return;
    }
    this.historyManager.movePolygonCorner(
      markerId,
      oldCoordinate,
      newCoordinate,
      oldPolygon,
      newPolygon
    );
  }

  moveLineEndpoint(
    markerId: string,
    oldCoordinate: ICoordinate,
    newCoordinate: ICoordinate,
    affectedLines: Array<{ oldLine: ILineConfig; newLine: ILineConfig }>
  ): void {
    if (!this.historyManager?.isEnabled) {
      const marker = this.markerManager?.getMarker(markerId);
      if (marker) {
        const updatedMarker = { ...marker, coordinate: newCoordinate };
        this.markerManager?.updateMarker(updatedMarker);
      }
      if (this.lineManager) {
        affectedLines.forEach(({ newLine }) => {
          this.lineManager!.updateLine(newLine);
        });
      }
      return;
    }
    this.historyManager.moveLineEndpoint(markerId, oldCoordinate, newCoordinate, affectedLines);
  }

  addLine(line: ILineConfig): void {
    if (!this.lineManager) {
      console.warn('Line manager not initialized. Enable lines in features config.');
      return;
    }

    this.executeWhenReady(() => {
      if (this.historyManager?.isEnabled) {
        this.historyManager.addLine(line);
      } else {
        this.lineManager!.addLine(line);
      }
    });
  }

  removeLine(lineId: string): void {
    if (!this.lineManager) {
      console.warn('Line manager not initialized.');
      return;
    }

    if (this.historyManager?.isEnabled) {
      this.historyManager.removeLine(lineId);
    } else {
      this.lineManager.removeLine(lineId);
    }
  }

  updateLine(line: ILineConfig): void {
    if (!this.lineManager) {
      console.warn('Line manager not initialized.');
      return;
    }

    this.executeWhenReady(() => {
      if (this.historyManager?.isEnabled) {
        this.historyManager.updateLine(line);
      } else {
        this.lineManager!.updateLine(line);
      }
    });
  }

  getLines(): ILineConfig[] {
    if (!this.lineManager) return [];
    return this.lineManager.getLines();
  }

  getLine(lineId: string): ILineConfig | undefined {
    if (!this.lineManager) return undefined;
    return this.lineManager.getLine(lineId);
  }

  clearLines(): void {
    if (!this.lineManager) return;
    this.lineManager.clearLines();
  }

  addPolygon(polygon: IPolygonConfig): void {
    if (!this.polygonManager) {
      console.warn('Polygon manager not initialized. Enable polygons in features config.');
      return;
    }

    this.executeWhenReady(() => {
      if (this.historyManager?.isEnabled) {
        this.historyManager.addPolygon(polygon);
      } else {
        this.polygonManager!.addPolygon(polygon);
      }
    });
  }

  removePolygon(polygonId: string): void {
    if (!this.polygonManager) {
      console.warn('Polygon manager not initialized.');
      return;
    }

    if (this.historyManager?.isEnabled) {
      this.historyManager.removePolygon(polygonId);
    } else {
      this.polygonManager.removePolygon(polygonId);
    }
  }

  updatePolygon(polygon: IPolygonConfig): void {
    if (!this.polygonManager) {
      console.warn('Polygon manager not initialized.');
      return;
    }

    this.executeWhenReady(() => {
      if (this.historyManager?.isEnabled) {
        this.historyManager.updatePolygon(polygon);
      } else {
        this.polygonManager!.updatePolygon(polygon);
      }
    });
  }

  getPolygons(): IPolygonConfig[] {
    if (!this.polygonManager) return [];
    return this.polygonManager.getPolygons();
  }

  getPolygon(polygonId: string): IPolygonConfig | undefined {
    if (!this.polygonManager) return undefined;
    return this.polygonManager.getPolygon(polygonId);
  }

  clearPolygons(): void {
    if (!this.polygonManager) return;
    this.polygonManager.clearPolygons();
  }

  clearFeatures(): void {
    if (this.historyManager?.isEnabled) {
      this.historyManager.clearAll();
    } else {
      this.clearMarkers();
      this.clearLines();
      this.clearPolygons();
    }
  }

  getFeatures(): IMapFeatures {
    return {
      markers: this.getMarkers(),
      lines: this.getLines(),
      polygons: this.getPolygons(),
    };
  }

  setFeatures(features: IMapFeatures): void {
    this.clearFeatures();

    this.executeWhenReady(() => {
      features.markers.forEach(marker => this.addMarker(marker));
      features.lines.forEach(line => this.addLine(line));
      features.polygons.forEach(polygon => this.addPolygon(polygon));
    });
  }

  getMarkerManager(): MarkerManager | undefined {
    return this.markerManager;
  }

  selectMarker(markerId: string): void {
    if (this.markerManager) {
      this.markerManager.selectMarker(markerId);
    }
  }

  deselectAllMarkers(): void {
    if (this.markerManager) {
      this.markerManager.deselectAllMarkers();
    }
  }

  hoverMarker(markerId: string | null): void {
    if (this.markerManager) {
      this.markerManager.hoverMarker(markerId);
    }
  }

  addMarkerEventListener(type: string, listener: EventListener): void {
    if (this.markerManager) {
      this.markerManager.addEventListener(type, listener);
    }
  }

  removeMarkerEventListener(type: string, listener: EventListener): void {
    if (this.markerManager) {
      this.markerManager.removeEventListener(type, listener);
    }
  }

  getLineManager(): LineManager | undefined {
    return this.lineManager;
  }

  getPolygonManager(): PolygonManager | undefined {
    return this.polygonManager;
  }

  getHistoryManager(): HistoryFeatureManager | undefined {
    return this.historyManager;
  }

  getCoreHistoryManager(): HistoryManager | undefined {
    return this.historyManager?.historyManager;
  }

  enableHistory(): void {
    if (!this.historyManager) {
      console.warn('History manager not initialized. Enable history in features config.');
      return;
    }
    this.historyManager.enable();
  }

  disableHistory(): void {
    if (!this.historyManager) return;
    this.historyManager.disable();
  }

  clearHistory(): void {
    if (!this.historyManager) return;
    this.historyManager.clear();
  }

  undo(): boolean {
    if (!this.historyManager) {
      console.warn('History manager not initialized.');
      return false;
    }
    return this.historyManager.undo();
  }

  redo(): boolean {
    if (!this.historyManager) {
      console.warn('History manager not initialized.');
      return false;
    }
    return this.historyManager.redo();
  }

  get canUndo(): boolean {
    return this.historyManager?.canUndo || false;
  }

  get canRedo(): boolean {
    return this.historyManager?.canRedo || false;
  }

  isHistoryEnabled(): boolean {
    return this.historyManager?.isEnabled || false;
  }

  setHistoryEnabled(enabled: boolean): void {
    if (this.historyManager) {
      if (enabled) {
        this.historyManager.enable();
      } else {
        this.historyManager.disable();
      }
    }
  }

  getUndoDescription(): string | null {
    return this.historyManager?.getUndoDescription() || null;
  }

  getRedoDescription(): string | null {
    return this.historyManager?.getRedoDescription() || null;
  }

  addMarkerWithLine(marker: IMarkerConfig, line: ILineConfig): void {
    if (!this.historyManager?.isEnabled) {
      this.executeWhenReady(() => {
        this.addMarker(marker);
        this.addLine(line);
      });
      return;
    }
    this.executeWhenReady(() => {
      this.historyManager!.addMarkerWithLine(marker, line);
    });
  }

  removeMarkerWithLine(marker: IMarkerConfig, line: ILineConfig): void {
    if (!this.historyManager?.isEnabled) {
      this.removeLine(line.id);
      this.removeMarker(marker.id);
      return;
    }
    this.historyManager.removeMarkerWithLine(marker, line);
  }

  addPolygonWithMarkers(polygon: IPolygonConfig, markers: IMarkerConfig[]): void {
    if (!this.historyManager?.isEnabled) {
      this.executeWhenReady(() => {
        this.addPolygon(polygon);
        markers.forEach(marker => this.addMarker(marker));
      });
      return;
    }
    this.executeWhenReady(() => {
      this.historyManager!.addPolygonWithMarkers(polygon, markers);
    });
  }

  removePolygonWithMarkers(polygon: IPolygonConfig, markers: IMarkerConfig[]): void {
    if (!this.historyManager?.isEnabled) {
      markers.forEach(marker => this.removeMarker(marker.id));
      this.removePolygon(polygon.id);
      return;
    }
    this.historyManager.removePolygonWithMarkers(polygon, markers);
  }

  addLineWithEndpoints(
    line: ILineConfig,
    startMarker: IMarkerConfig,
    endMarker: IMarkerConfig
  ): void {
    if (!this.historyManager?.isEnabled) {
      this.addMarker(startMarker);
      this.addMarker(endMarker);
      this.addLine(line);
      return;
    }
    this.historyManager.addLineWithEndpoints(line, startMarker, endMarker);
  }

  removeLineWithEndpoints(
    line: ILineConfig,
    startMarker: IMarkerConfig,
    endMarker: IMarkerConfig
  ): void {
    if (!this.historyManager?.isEnabled) {
      this.removeLine(line.id);
      this.removeMarker(startMarker.id);
      this.removeMarker(endMarker.id);
      return;
    }
    this.historyManager.removeLineWithEndpoints(line, startMarker, endMarker);
  }

  addPolygonCorner(marker: IMarkerConfig, polygon: IPolygonConfig): void {
    if (!this.historyManager?.isEnabled) {
      this.addMarker(marker);
      if (this.polygonManager?.getPolygon(polygon.id)) {
        this.removePolygon(polygon.id);
      }
      this.addPolygon(polygon);
      return;
    }
    this.historyManager.addPolygonCorner(marker, polygon);
  }

  removePolygonCorner(
    marker: IMarkerConfig,
    oldPolygon: IPolygonConfig,
    newPolygon?: IPolygonConfig
  ): void {
    if (!this.historyManager?.isEnabled) {
      this.removeMarker(marker.id);
      this.removePolygon(oldPolygon.id);
      if (newPolygon) {
        this.addPolygon(newPolygon);
      }
      return;
    }
    this.historyManager.removePolygonCorner(marker, oldPolygon, newPolygon);
  }

  addLineSegment(
    marker: IMarkerConfig,
    newLines: ILineConfig[],
    removedLines?: ILineConfig[]
  ): void {
    if (!this.historyManager?.isEnabled) {
      if (this.markerManager) {
        this.markerManager.addMarker(marker);
      }
      if (this.lineManager) {
        removedLines?.forEach(line => this.lineManager!.removeLine(line.id));
        newLines.forEach(line => this.lineManager!.addLine(line));
      }
      return;
    }
    this.historyManager.addLineSegment(marker, newLines, removedLines);
  }

  removeLinePoint(
    marker: IMarkerConfig,
    removedLines: ILineConfig[],
    newLines?: ILineConfig[]
  ): void {
    if (!this.historyManager?.isEnabled) {
      if (this.markerManager) {
        this.markerManager.removeMarker(marker.id);
      }
      if (this.lineManager) {
        removedLines.forEach(line => this.lineManager!.removeLine(line.id));
        newLines?.forEach(line => this.lineManager!.addLine(line));
      }
      return;
    }
    this.historyManager.removeLinePoint(marker, removedLines, newLines);
  }

  removePoint(marker: IMarkerConfig): void {
    if (!this.historyManager?.isEnabled) {
      if (this.markerManager) {
        this.markerManager.removeMarker(marker.id);
      }
      return;
    }
    this.historyManager.removePoint(marker);
  }

  getFeatureStats(): {
    markers: { total: number; selected: number } | null;
    lines: { total: number; totalLength: number; averageLength: number } | null;
    polygons: { total: number; totalArea: number; averageArea: number } | null;
  } {
    return {
      markers: this.markerManager?.getStats() || null,
      lines: this.lineManager?.getStats() || null,
      polygons: this.polygonManager?.getStats() || null,
    };
  }
}
